import R from 'ramda';
import React from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { connect } from 'react-redux';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import Heading from '@cotyorg/ccx-atoms/Heading';
import CardoGroup from '@cotyorg/ccx-organism-cardo-group/components/CardoGroup';
import { CardsCarousel } from '@cotyorg/ccx-organism-carousel';
import Breadcrumbs from '@cotyorg/ccx-molecules/Breadcrumbs';
import { HeroFullBlock } from '@cotyorg/ccx-organism-hero/';
import { VideoPlayer } from '@cotyorg/ccx-organism-video';
import {
  trackVideoPlayEvent,
  trackVideoMilestoneEvent,
} from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/video';
import { trackCarouselClickEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/carousel';
import StandardLayout from '../../templates/StandardLayout';
import SectionText from '../../molecules/SectionText';
import { cardsSectionBreakpointConfig } from './cardo.config';

const generateCarouselGridCssClass = ({ cardsLength }) =>
  className('cell', 'xsmall-14 xsmall-offset-1', {
    'large-4 large-offset-6': cardsLength === 1,
    'large-8 large-offset-4': cardsLength === 2,
    'large-12 large-offset-2': cardsLength === 3,
  });

export const TrendsArticleComponent = ({
  sectionHeadings,
  breadcrumb,
  textSection,
  cardsSection,
  productCarouselSection,
  videos,
}) => (
  <StandardLayout>
    <div className="main-section">
      <Breadcrumbs items={breadcrumb} />
      <div className="grid-container">
        <div className="grid-x">
          <div className="cell xsmall-16">
            <HeroFullBlock
              layout="vertical"
              isContentFirst
              heroStoreDataKey="heroSection"
            />
          </div>
        </div>
      </div>
    </div>

    <OptionalSection
      renderCondition={!R.isEmpty(R.prop('textSection', textSection))}
      section={() => (
        <div className="grid-container">
          <div className="grid-x grid-padding-x">
            <div className="cell xsmall-16 medium-10 medium-offset-3">
              <div className="trends-article-text-section text--center">
                <SectionText>{R.prop('textSection', textSection)}</SectionText>
              </div>
            </div>
          </div>
        </div>
      )}
    />

    <OptionalSection
      renderCondition={!R.isEmpty(cardsSection)}
      section={() => (
        <div className="trends-article-section text--center">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="cell xsmall-16 large-14 large-offset-1">
                <Heading level={2} extraClass="trends-article-section-heading">
                  {R.path(['cardsSection', 'heading'], sectionHeadings)}
                </Heading>

                <div className="grid-x grid-padding-x flex-container align-center">
                  <CardoGroup
                    data="cardsSection"
                    isTitleUnderlined={false}
                    contentTheme="transparent"
                    headingLevel={5}
                    fitImageToCard
                    baseBreakpointConfig={cardsSectionBreakpointConfig}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    />

    <OptionalSection
      renderCondition={!R.isEmpty(R.path(['mainVideo', 'url'], videos))}
      section={() => (
        <div className="grid-container">
          <div className="grid-x grid-padding-x">
            <div className="cell xsmall-16 medium-14 medium-offset-1">
              <div className="trends-article-youtube-video">
                <div className="youtube-video">
                  <VideoPlayer
                    videosStoreDataKey="mainVideo"
                    showControls={false}
                    title={R.path(['mainVideo', 'name'], videos)}
                    shouldUseDefaultTitle
                    onProgress={({
                      url,
                      duration,
                      progress,
                      videoMilestone,
                      title,
                    }) =>
                      trackVideoMilestoneEvent({
                        url,
                        duration,
                        progress,
                        videoMilestone,
                        title,
                      })
                    }
                    onPlay={({ url, duration, progress, title }) =>
                      trackVideoPlayEvent({
                        url,
                        duration,
                        progress,
                        title,
                      })
                    }
                    config={{
                      youtube: {
                        playerVars: {
                          controls: 1,
                        },
                        embedOptions: {
                          host: 'https://www.youtube-nocookie.com',
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    />

    <OptionalSection
      renderCondition={!R.isEmpty(productCarouselSection)}
      section={() => (
        <div className="products-section text--center">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="cell xsmall-16">
                <Heading level={2} extraClass="products-section-heading">
                  {R.path(
                    ['productCarouselSection', 'heading'],
                    sectionHeadings
                  )}
                </Heading>
                <div className="grid-x">
                  <div
                    className={generateCarouselGridCssClass({
                      cardsLength: R.length(productCarouselSection),
                    })}
                  >
                    <CardsCarousel
                      cards={{
                        cardsStoreDataKey: 'productCarouselSection',
                        isTitleUnderlined: false,
                        hasBackground: false,
                        isSmallTitle: true,
                        fullWidth: false,
                        headingLevel: 5,
                        showStarRating: false,
                        fixedHeight: true,
                        zoomOnHover: true,
                        alignCenter: true,
                      }}
                      carousel={{
                        carouselsStoreDataKey: 'productCarouselSection',
                        headingLevel: 5,
                        rightChevronIconSrc:
                          '/ccx-files/assets/chevron-right.svg',
                        leftChevronIconSrc:
                          '/ccx-files/assets/chevron-left.svg',
                        onIndexChange: ({ type, index, isForward }) =>
                          trackCarouselClickEvent({
                            page: 'trendsArticle',
                            carousel: 'productCarouselSection',
                            type,
                            index,
                            isForward,
                          }),
                      }}
                      cardsPerPageMap={{
                        xsmall: 1,
                        small: 2,
                        medium: 3,
                        large: R.length(productCarouselSection),
                        xlarge: R.length(productCarouselSection),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    />
  </StandardLayout>
);

TrendsArticleComponent.propTypes = {
  sectionHeadings: PropTypes.objectOf(PropTypes.object),
  breadcrumb: PropTypes.arrayOf(PropTypes.object),
  textSection: PropTypes.objectOf(PropTypes.string),
  cardsSection: PropTypes.arrayOf(PropTypes.object),
  productCarouselSection: PropTypes.arrayOf(PropTypes.object),
  videos: PropTypes.objectOf(PropTypes.object),
};

export default connect(({ content }) => ({
  breadcrumb: R.prop('breadcrumb', content),
  sectionHeadings: R.prop('sectionHeadings', content),
  textSection: R.prop('textSection', content),
  pictures: R.prop('pictures', content),
  cardsSection: R.path(['cardoGroup', 'cardsSection'], content),
  productCarouselSection: R.path(['cards', 'productCarouselSection'], content),
  videos: R.prop('videos', content),
}))(TrendsArticleComponent);
